import { helpers } from '@vuelidate/validators';
import { MEDIA_CONFIG, VALIDATION_CONSTANTS } from './constants.js';

export const extractVariables = text => {
  if (!text) return [];
  const matches = text.match(/\{\{(\d+)\}\}/g) || [];
  return matches.map(m => parseInt(m.replace(/\{|\}/g, ''), 10));
};

// Template name must contain only lowercase letters, numbers, and underscores
export const templateNameValidator = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.TEMPLATE_NAME_FORMAT_ERROR',
  value => /^[a-z0-9_]+$/.test(value)
);

export const isValidHeaderFile = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.INVALID_HEADER_FILE_ERROR',
  (file, siblings) => {
    if (!['IMAGE', 'VIDEO', 'DOCUMENT'].includes(siblings.header)) return true;
    return (
      MEDIA_CONFIG[siblings.header].types.includes(file.type) &&
      file.size <= MEDIA_CONFIG[siblings.header].maxSize
    );
  }
);
// Variables must be sequential ({{1}}, {{2}}, {{3}}...)
export const sequentialVariables = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.VARIABLES_SEQUENTIAL_ERROR',
  value => {
    const vars = extractVariables(value);
    for (let i = 0; i < vars.length; i += 1) {
      if (vars[i] !== i + 1) return false;
    }
    return true;
  }
);

// Variables cannot be adjacent (e.g., {{1}} {{2}}). Add text between them
export const noAdjacentVariables = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.VARIABLES_ADJACENT_ERROR',
  value => {
    if (!value) return true;
    return !/\{\{\d+\}\}\s+\{\{\d+\}\}/.test(value);
  }
);

// Cannot start or end with a variable
export const noStartEndVariable = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.VARIABLES_START_END_ERROR',
  (value, siblings) => {
    if (siblings?.category === 'AUTHENTICATION') return true;
    if (!value) return true;
    const trimmed = value.trim();
    return !trimmed.startsWith('{{') && !trimmed.endsWith('}}');
  }
);

// Maximum allowed 1 variable ({{1}} only)
export const onlyOneVariable = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.HEADER_VARIABLE_LIMIT_ERROR',
  value => extractVariables(value).length <= 1
);

// Please enter valid values for all variables
export const allKeysRequired = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.VARIABLES_REQUIRED_ERROR',
  value => {
    const keys = Object.keys(value);
    return keys.every(key => value[key]);
  }
);

// URL validator
export const validUrl = helpers.withMessage(
  'Please enter a valid URL',
  value => {
    if (!value) return true;
    // Allow {{1}} in URL
    const urlWithoutVar = value.replace(/\{\{1\}\}/g, 'placeholder');
    try {
      return Boolean(new URL(urlWithoutVar));
    } catch {
      return false;
    }
  }
);

// No emojis allowed
export const buttonTextFormat = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.BUTTON_TEXT_FORMAT_ERROR',
  value => !value || /^[a-zA-Z0-9\s.,!?'-]*$/.test(value)
);

// Phone format: +[country code][number]. E.g., +1234567890. No spaces/dashes.
export const phoneFormat = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.PHONE_FORMAT_ERROR',
  value => {
    if (!value) return true;
    if (!value.startsWith('+')) return false;
    const digits = value.slice(1);
    if (
      digits.length < 7 ||
      digits.length > VALIDATION_CONSTANTS.MAX_PHONE_LENGTH
    )
      return false;
    return /^\d+$/.test(digits);
  }
);

// Variable {{1}} must be at the End of URL only
export const urlVariableAtEndValidator = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.URL_VARIABLE_POSITION_ERROR',
  value => {
    if (!value) return true;
    if (!value.includes('{{')) return true; // No variables = valid
    return VALIDATION_CONSTANTS.DYNAMIC_VARIABLE_PATTERN.test(value);
  }
);

// Only {{1}} variable validator (// URL buttons support only {{1}} variable, no other variables)
export const onlyOneUrlVariableValidator = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.URL_VARIABLE_LIMIT_ERROR',
  value => {
    if (!value) return true;
    const matches = value.match(/\{\{\d+\}\}/g) || [];
    if (matches.length === 0) return true;
    return matches.length === 1 && value.includes('{{1}}');
  }
);

// URL suffix example is required for dynamic URLs
export const urlSuffixRequiredValidator = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.URL_SUFFIX_REQUIRED_ERROR',
  (value, button) => {
    if (!button.baseUrl || !button.baseUrl.includes('{{1}}')) return true;
    return !!value && value.trim().length > 0;
  }
);

// No duplicate button texts
export const noDuplicateQuickReplyTexts = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.DUPLICATE_BUTTON_TEXT_ERROR',
  buttons => {
    if (!buttons || buttons.length === 0) return true;
    const texts = buttons.map(b => b.text?.trim().toLowerCase());
    const uniqueTexts = new Set(texts);
    return uniqueTexts.size === texts.length;
  }
);

// Button count validator
export const quickReplyCountValidator = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.QUICK_REPLY_COUNT_ERROR',
  buttons => {
    return (
      !buttons || buttons.length <= VALIDATION_CONSTANTS.MAX_QUICK_REPLY_BUTTONS
    );
  }
);

// Total button count validator
export const ctaTotalButtonsValidator = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.CTA_BUTTON_COUNT_ERROR',
  buttons => {
    return !buttons || buttons.length <= VALIDATION_CONSTANTS.MAX_CTA_BUTTONS;
  }
);

// Max phone buttons validator
export const ctaMaxPhoneValidator = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.PHONE_BUTTON_LIMIT_ERROR',
  buttons => {
    if (!buttons) return true;
    const phoneCount = buttons.filter(b => b.actionType?.id === 'PHONE').length;
    return phoneCount <= VALIDATION_CONSTANTS.MAX_PHONE_BUTTONS;
  }
);

// Max URL buttons validator
export const ctaMaxUrlValidator = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.URL_BUTTON_LIMIT_ERROR',
  buttons => {
    if (!buttons) return true;
    const urlCount = buttons.filter(b => b.actionType?.id === 'URL').length;
    return urlCount <= VALIDATION_CONSTANTS.MAX_URL_BUTTONS;
  }
);

// Carousel cards validator
export const validateCarouselCards = helpers.withMessage(
  'WHATSAPP_TEMPLATES.ADD.ERRORS.CAROUSEL_CARDS_ERROR',
  cards => {
    if (!cards) return false;
    // Must have between min and max cards
    if (
      cards.length < VALIDATION_CONSTANTS.MIN_CAROUSEL_CARDS ||
      cards.length > VALIDATION_CONSTANTS.MAX_CAROUSEL_CARDS
    )
      return false;

    // Each card must have a specific structure (simplified check)
    // We expect header media and body text to be present or valid
    // This can be expanded based on specific requirements
    return cards.every(card => {
      // Check if body text exists and is within limits (assuming limits are checked elsewhere or here)
      const hasBody = card.body && card.body.trim().length > 0;

      // Check if header media is selected (file or url if checking for content) - optional depending on strictness
      // For now, let's assume validation is satisfied if we have valid card structure
      // Detailed field validation (like max length) is usually handled on the individual field in the editor
      return hasBody;
    });
  }
);
